import { useCallback, useEffect, useState } from 'react';
import { decodeJwt } from 'jose';
import { ConnectionDetails } from '@/app/api/connection-details/route';
import { patientApi } from '@/lib/api-client';
import { AppConfig } from '@/lib/types';

const ONE_MINUTE_IN_MILLISECONDS = 60 * 1000;

export interface UseConnectionDetailsOptions {
  /** When true, do not read/send patient_session_id (e.g. free-chat demo page). */
  skipPatientSession?: boolean;
}

export default function useConnectionDetails(
  appConfig: AppConfig,
  options?: UseConnectionDetailsOptions
) {
  // Generate room connection details, including:
  //   - A random Room name
  //   - A random Participant name
  //   - An Access Token to permit the participant to join the room
  //   - The URL of the LiveKit server to connect to
  //
  // In real-world application, you would likely allow the user to specify their
  // own participant name, and possibly to choose from existing rooms to join.

  const [connectionDetails, setConnectionDetails] = useState<ConnectionDetails | null>(null);
  const skipPatientSession = options?.skipPatientSession === true;

  const fetchConnectionDetails = useCallback(async () => {
    console.log('========================================');
    console.log('[useConnectionDetails] fetchConnectionDetails called!');
    console.log('========================================');

    setConnectionDetails(null);
    const url = new URL(
      process.env.NEXT_PUBLIC_CONN_DETAILS_ENDPOINT ?? '/api/connection-details',
      window.location.origin
    );

    // Get patient session_id from sessionStorage if available (unless skipPatientSession, e.g. free-chat demo)
    let patientSessionId: number | null = null;
    let patientIdCard: string | null = null;
    if (!skipPatientSession) {
      try {
        const patientSessionInfo = sessionStorage.getItem('patient_session_info');
        console.log(
          '[useConnectionDetails] Raw patient_session_info from sessionStorage:',
          patientSessionInfo
        );
        if (patientSessionInfo) {
          const parsed = JSON.parse(patientSessionInfo);
          console.log('[useConnectionDetails] Parsed patient_session_info:', parsed);
          console.log(
            '[useConnectionDetails] Has patient_session_id?',
            !!parsed.patient_session_id
          );
          // Use patient_session_id (the primary key of patient_sessions table) not session_id (LiveKit room name)
          patientSessionId = parsed.patient_session_id || null;
          console.log(
            '[useConnectionDetails] Extracted patient_session_id:',
            patientSessionId,
            'type:',
            typeof patientSessionId
          );
        }
        // Get patient ID card for session linking
        patientIdCard = sessionStorage.getItem('patient_id_card');
        console.log('[useConnectionDetails] patient_id_card:', patientIdCard);
      } catch (e) {
        console.error('[useConnectionDetails] Failed to read patient session info:', e);
      }
    } else {
      console.log('[useConnectionDetails] skipPatientSession=true, not sending patient_session_id');
    }

    console.log('[useConnectionDetails] About to fetch from:', url.toString());
    console.log(
      '[useConnectionDetails] Request body will include patient_session_id:',
      patientSessionId
    );

    let data: ConnectionDetails;
    try {
      const res = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Sandbox-Id': appConfig.sandboxId ?? '',
        },
        body: JSON.stringify({
          room_config: appConfig.agentName
            ? {
                agents: [{ agent_name: appConfig.agentName }],
              }
            : undefined,
          patient_session_id: patientSessionId,
        }),
      });
      console.log('[useConnectionDetails] Fetch completed, status:', res.status);
      data = await res.json();
      console.log('[useConnectionDetails] Response data:', data);
    } catch (error) {
      console.error('[useConnectionDetails] Error fetching connection details:', error);
      throw new Error('Error fetching connection details!');
    }

    // Link session to patient if patient is logged in (skip when free-chat demo)
    if (!skipPatientSession && patientIdCard && data.roomName) {
      try {
        await patientApi.linkSession({
          session_id: data.roomName,
          patient_id_card: patientIdCard,
        });
        console.log('✅ Session linked to patient:', data.roomName);
      } catch (error) {
        console.warn('⚠️ Failed to link session to patient:', error);
        // Don't throw error, allow connection to proceed
      }
    }

    setConnectionDetails(data);
    return data;
  }, [skipPatientSession]);

  useEffect(() => {
    fetchConnectionDetails();
  }, [fetchConnectionDetails]);

  const isConnectionDetailsExpired = useCallback(() => {
    const token = connectionDetails?.participantToken;
    if (!token) {
      return true;
    }

    const jwtPayload = decodeJwt(token);
    if (!jwtPayload.exp) {
      return true;
    }
    const expiresAt = new Date(jwtPayload.exp - ONE_MINUTE_IN_MILLISECONDS);

    const now = new Date();
    return expiresAt >= now;
  }, [connectionDetails?.participantToken]);

  const existingOrRefreshConnectionDetails = useCallback(async () => {
    if (isConnectionDetailsExpired() || !connectionDetails) {
      return fetchConnectionDetails();
    } else {
      return connectionDetails;
    }
  }, [connectionDetails, fetchConnectionDetails, isConnectionDetailsExpired]);

  return {
    connectionDetails,
    refreshConnectionDetails: fetchConnectionDetails,
    existingOrRefreshConnectionDetails,
  };
}
